/**
 * The night behind the world, one per viewport, in screen space: a sky that darkens toward the
 * zenith and glows at the horizon, a few twinkling stars, a small striped sun low on the right, a
 * dim ridge of mountains, and (off unless the owner turns it on) a floor grid under the horizon.
 * Kept plain on purpose (the owner, 2026-09-20: "make the background simpler"): the world's lit
 * edges and the paper body are what must read. Each layer follows the camera at its own fraction
 * (a parallax), so the world has depth and speed reads from the layers sliding past each other.
 * Everything is created once and moved in place; the frame allocates nothing.
 */

import { Container, Sprite } from "pixi.js";
import type { Camera } from "./camera";
import type { RenderFeel } from "./feel";
import { NEON } from "./palette";
import { type GradientStop, mixColor, RIDGE_TILE_H, RIDGE_TILE_W, type ShapeFactory } from "./shapes";

const STAR_COUNT = 48;
const SUN_BANDS = 40;
/**
 * Copies of the ridge tile laid side by side, scrolled and wrapped by hand. A TilingSprite would
 * say the same thing in one object, but Pixi batches it and the batch ignores the texture's repeat
 * address mode: the skyline drew once and then clamped its last column into a flat line, one tile
 * into a course (the owner, 2026-09-20). Eight covers a viewport ten times wider than it is tall;
 * the ones past the right edge are hidden, and none of them is ever created in a frame.
 */
const RIDGE_TILES = 8;
const FLOOR_ROWS = 9;
const FLOOR_COLUMNS = 64;
/**
 * The sky in two pieces, each stretched over exactly its part of the viewport (above the horizon,
 * below it), so no fragment is drawn outside the rectangle: software GL (the e2e suite, a weak
 * phone) pays per pixel filled, and a split-screen half must not paint under the other half.
 */
const SKY_ABOVE: readonly GradientStop[] = [
  [0, NEON.skyTop],
  [0.6, NEON.skyMid],
  [0.88, NEON.skyLow],
  [1, NEON.horizon],
];
const SKY_BELOW: readonly GradientStop[] = [
  [0, NEON.horizon],
  [0.06, NEON.belowHorizon],
  [1, NEON.night],
];

function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function wrap(v: number, period: number): number {
  return v - Math.floor(v / period) * period;
}

export class Backdrop {
  readonly root = new Container();
  private readonly skyAbove: Sprite;
  private readonly skyBelow: Sprite;
  private readonly horizonGlow: Sprite;
  private readonly stars: Sprite[] = [];
  private readonly starFx = new Float64Array(STAR_COUNT);
  private readonly starFy = new Float64Array(STAR_COUNT);
  private readonly starPhase = new Float64Array(STAR_COUNT);
  private readonly starSpeed = new Float64Array(STAR_COUNT);
  private readonly starSize = new Float64Array(STAR_COUNT);
  private readonly sunGlow: Sprite;
  private readonly sunBands: Sprite[] = [];
  private readonly ridges: Sprite[] = [];
  private readonly floorRows: Sprite[] = [];
  private readonly floorColumns: Sprite[] = [];
  private width = 0;
  private height = 0;

  constructor(f: ShapeFactory) {
    const r = this.root;
    this.skyAbove = new Sprite(f.gradient(SKY_ABOVE));
    this.skyBelow = new Sprite(f.gradient(SKY_BELOW));
    r.addChild(this.skyAbove);
    r.addChild(this.skyBelow);
    this.horizonGlow = f.halo(r, 0, 0, 1, NEON.pink, 0.16);
    const rnd = lcg(2026);
    for (let i = 0; i < STAR_COUNT; i++) {
      this.starFx[i] = rnd();
      this.starFy[i] = 0.02 + rnd() * 0.9;
      this.starPhase[i] = rnd() * Math.PI * 2;
      this.starSpeed[i] = 0.6 + rnd() * 1.8;
      this.starSize[i] = rnd();
      this.stars.push(f.disc(r, 0, 0, 1, 0xffffff, 0.8));
    }
    this.sunGlow = f.halo(r, 0, 0, 1, NEON.orange, 0.22);
    for (let i = 0; i < SUN_BANDS; i++) {
      const color = mixColor(NEON.sunTop, NEON.sunBottom, i / (SUN_BANDS - 1));
      this.sunBands.push(f.rect(r, 0, 0, 1, 1, color));
    }
    for (let i = 0; i < RIDGE_TILES; i++) {
      const tile = new Sprite(f.ridge);
      tile.anchor.set(0, 0);
      r.addChild(tile);
      this.ridges.push(tile);
    }
    for (let i = 0; i < FLOOR_ROWS; i++) this.floorRows.push(f.rect(r, 0, 0, 1, 1, NEON.cyan, 0.1));
    for (let i = 0; i < FLOOR_COLUMNS; i++) this.floorColumns.push(f.rect(r, 0, 0, 1, 1, NEON.cyan, 0.1));
  }

  /** Place every layer for this frame from the camera (its rectangle, position, and scale). */
  update(camera: Camera, feel: RenderFeel, nowMs: number, originX = 0): void {
    const W = camera.screenW;
    const H = camera.screenH;
    if (W !== this.width || H !== this.height) {
      this.width = W;
      this.height = H;
    }
    const ppm = camera.ppm;
    const cameraX = camera.x + originX;
    const s = H / 1080;
    // The horizon sinks as the camera climbs, at its own fraction of the world's motion.
    const hy = H * feel.horizonFrac + camera.y * feel.horizonParallax * ppm;

    const above = Math.min(H, Math.max(0, hy));
    this.skyAbove.visible = above > 0.5;
    this.skyAbove.position.set(0, 0);
    this.skyAbove.width = W;
    this.skyAbove.height = Math.max(1, above);
    this.skyBelow.visible = above < H - 0.5;
    this.skyBelow.position.set(0, above);
    this.skyBelow.width = W;
    this.skyBelow.height = Math.max(1, H - above);
    // The glow sits on the horizon, most of it above: below the horizon the night stays dark.
    this.horizonGlow.position.set(W * 0.5, hy - 0.07 * H);
    this.horizonGlow.width = W * 1.25;
    this.horizonGlow.height = H * 0.3;

    const fieldW = 2 * W;
    const starShift = cameraX * feel.starParallax * ppm;
    const t = nowMs * 0.001;
    for (let i = 0; i < STAR_COUNT; i++) {
      const star = this.stars[i]!;
      const x = wrap(this.starFx[i]! * fieldW - starShift, fieldW) - W * 0.5;
      const y = this.starFy[i]! * (hy - 6 * s);
      const visible = y >= 0 && y < hy - 2;
      star.visible = visible;
      if (!visible) continue;
      const size = (1.2 + 2.2 * this.starSize[i]!) * s;
      star.position.set(x, y);
      star.width = size;
      star.height = size;
      star.alpha = 0.3 + 0.7 * (0.5 + 0.5 * Math.sin(t * this.starSpeed[i]! + this.starPhase[i]!));
    }

    // The sun sits on the horizon, its lower part behind the ridges and nothing of it below the
    // horizon line (the sky under the horizon shows wherever the ground is thin).
    const R = feel.sunRadius * H;
    const sx = W * feel.sunX - cameraX * feel.sunParallax * ppm;
    const sy = hy + 0.1 * R;
    this.sunGlow.position.set(sx, sy - 0.3 * R);
    this.sunGlow.width = R * 3.2;
    this.sunGlow.height = R * 2.4;
    const bandH = (2 * R) / SUN_BANDS;
    for (let i = 0; i < SUN_BANDS; i++) {
      const band = this.sunBands[i]!;
      const lower = i / SUN_BANDS > 0.5;
      const yTop = sy - R + i * bandH;
      const dy = yTop + bandH * 0.5 - sy;
      const half = Math.sqrt(Math.max(0, R * R - dy * dy));
      // The lower half is striped: every other band is a gap, and the bands thin toward the bottom.
      const h = lower ? bandH * 0.7 : bandH;
      const visible = half > 0.5 && !(lower && (i & 1) === 1) && yTop + h <= hy;
      band.visible = visible;
      if (!visible) continue;
      band.position.set(sx - half, yTop);
      band.width = 2 * half;
      band.height = h;
    }

    // The ridge: one tile after another from the left edge, the row slid by the parallax and
    // wrapped a tile at a time, so the skyline keeps going however far into a course the camera is.
    const ridgeScale = s * 0.8;
    const ridgeH = RIDGE_TILE_H * ridgeScale;
    const tileW = RIDGE_TILE_W * ridgeScale;
    const ridgeY = hy + 0.06 * H - ridgeH;
    const ridgeShift = wrap(cameraX * feel.ridgeParallax * ppm, tileW);
    for (let i = 0; i < RIDGE_TILES; i++) {
      const tile = this.ridges[i]!;
      const x = i * tileW - ridgeShift;
      const visible = x < W;
      tile.visible = visible;
      if (!visible) continue;
      tile.position.set(x, ridgeY);
      tile.width = tileW;
      tile.height = ridgeH;
    }

    // The floor grid: rows that crowd toward the horizon, columns sliding with the camera.
    const below = Math.max(0, H - hy);
    const gridOn = feel.floorGridAlpha > 0.001 && feel.floorGridPx * s >= 6 && below > 4;
    const line = Math.max(1, Math.round(s));
    for (let k = 0; k < FLOOR_ROWS; k++) {
      const row = this.floorRows[k]!;
      row.visible = gridOn;
      if (!gridOn) continue;
      const q = (k + 1) / FLOOR_ROWS;
      row.position.set(0, hy + below * q ** 1.7);
      row.width = W;
      row.height = line;
      row.alpha = feel.floorGridAlpha * (0.35 + 0.65 * q);
    }
    const step = feel.floorGridPx * s;
    const shift = gridOn ? wrap(cameraX * 0.5 * ppm, step) : 0;
    for (let j = 0; j < FLOOR_COLUMNS; j++) {
      const col = this.floorColumns[j]!;
      const x = j * step - shift;
      const visible = gridOn && x < W;
      col.visible = visible;
      if (!visible) continue;
      col.position.set(x, hy);
      col.width = line;
      col.height = below;
      col.alpha = feel.floorGridAlpha * 0.8;
    }
  }

  destroy(): void {
    this.root.parent?.removeChild(this.root);
    this.root.destroy({ children: true, texture: false, textureSource: false });
  }
}
